import { Controller } from "@hotwired/stimulus"

// Drives the Vehicle Localization wizard.
// Step 1 — cascading car → version → models pickers fed by JSON from
//          the controller's own #new action.
// Step 3 — bulk "apply to all" actions for the feature triage radios.
export default class extends Controller {
  static targets = [
    "carSelect", "versionList", "modelList",
    "sourceRegions", "targetRegion", "targetWarning",
    "modeRadio", "submitButton",
    "featureRow", "featureDecision",
    "playlistRow", "playlistOverrides"
  ]

  static values = {
    pickCarFirst:        { type: String, default: "Pick a car first." },
    pickRegionFirst:     { type: String, default: "Pick a target region." },
    loadError:           { type: String, default: "Failed to load versions." },
    noVersions:          { type: String, default: "No versions found." },
    noModels:            { type: String, default: "No models found." },
    pickVersionFirst:    { type: String, default: "Select at least one version." },
    sourceRegionsLabel:  { type: String, default: "Already available in:" },
    sourceRegionsNone:   { type: String, default: "This car has no regions yet." },
    overlayConflict:     { type: String, default: "This region is already covered by the source car — overlay would do nothing." },
    newCarConflict:      { type: String, default: "A car already exists for this region (%{cars}). Continuing will create another." },
    fullyLocalized:      { type: String, default: "This car is already fully localized for %{region}." }
  }

  connect() {
    if (this.hasCarSelectTarget && this.carSelectTarget.value) {
      this.loadStructure(this.carSelectTarget.value, this._currentTargetRegionId())
    }
  }

  carChanged(event) {
    const carId = event.currentTarget.value
    if (!carId) {
      if (this.hasVersionListTarget) {
        this.versionListTarget.innerHTML = this._empty(this.pickCarFirstValue)
      }
      if (this.hasModelListTarget) {
        this.modelListTarget.innerHTML = this._empty(this.pickCarFirstValue)
      }
      return
    }
    this.loadStructure(carId, this._currentTargetRegionId())
  }

  versionChanged() {
    this.renderModels()
  }

  loadStructure(carId, targetRegionId) {
    const params = new URLSearchParams({ car_id: carId })
    if (targetRegionId) params.set("target_region_id", targetRegionId)
    fetch(`/admin/vehicle_localizations/new.json?${params.toString()}`, {
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
    })
      .then(r => r.json())
      .then(data => {
        this._versions         = data.versions || []
        this._sourceRegions    = data.source_regions || []
        this._cloneRegions     = data.clone_regions || {}
        this._targetRegionName = data.target_region_name || ""
        this.renderVersions()
        this.renderModels()
        this.renderSourceRegions()
        this.refreshTargetWarning()
      })
      .catch(() => {
        if (this.hasModelListTarget) {
          this.modelListTarget.innerHTML =
            `<p class="text-xs text-error">${this._escape(this.loadErrorValue)}</p>`
        }
      })
  }

  _currentTargetRegionId() {
    if (!this.hasTargetRegionTarget) return null
    return this.targetRegionTarget.value || null
  }

  // Renders the "already available in: BR · AR · CL" chip row under
  // the source car select so the admin sees the starting state.
  renderSourceRegions() {
    if (!this.hasSourceRegionsTarget) return
    const codes = this._sourceRegions || []
    if (codes.length === 0) {
      this.sourceRegionsTarget.innerHTML =
        `<span class="text-base-content/40">${this._escape(this.sourceRegionsNoneValue)}</span>`
    } else {
      const chips = codes.map(c =>
        `<span class="badge badge-sm badge-outline badge-info font-semibold">${this._escape(c)}</span>`
      ).join(" ")
      this.sourceRegionsTarget.innerHTML =
        `<span class="text-base-content/60 mr-2">${this._escape(this.sourceRegionsLabelValue)}</span> ${chips}`
    }
    this.sourceRegionsTarget.classList.remove("hidden")
  }

  targetRegionChanged() {
    // Re-fetch the structure so versions/motorizations auto-filter to rows
    // not yet localized in the chosen target region. Fall back to just
    // refreshing the duplicate-warning alert when no car is picked yet.
    const carId = this.hasCarSelectTarget ? this.carSelectTarget.value : null
    if (carId) {
      this.loadStructure(carId, this._currentTargetRegionId())
    } else {
      this.refreshTargetWarning()
    }
  }

  refreshTargetWarning() {
    if (!this.hasTargetWarningTarget || !this.hasTargetRegionTarget) return
    const select = this.targetRegionTarget
    const code   = select.options[select.selectedIndex]?.text?.split(" — ")[0] || ""
    const mode   = this._currentMode()

    const warn = this.targetWarningTarget
    warn.innerHTML = ""
    warn.classList.add("hidden")
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false

    if (!code || !this._sourceRegions) return

    if (mode === "overlay" && this._sourceRegions.includes(code)) {
      warn.innerHTML =
        `<div class="alert alert-warning py-2 text-xs">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
          <span>${this._escape(this.overlayConflictValue)}</span>
        </div>`
      warn.classList.remove("hidden")
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true
      return
    }

    if (mode === "new_car" && this._cloneRegions && this._cloneRegions[code]) {
      const list = this._cloneRegions[code].map(c => c.name).join(", ")
      const msg  = this.newCarConflictValue.replace("%{cars}", list)
      warn.innerHTML =
        `<div class="alert alert-info py-2 text-xs">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" /></svg>
          <span>${this._escape(msg)}</span>
        </div>`
      warn.classList.remove("hidden")
    }
  }

  _currentMode() {
    if (!this.hasModeRadioTarget) return "overlay"
    const checked = this.modeRadioTargets.find(r => r.checked)
    return checked?.value || "overlay"
  }

  renderVersions() {
    if (!this.hasVersionListTarget) return
    if (!this._versions || this._versions.length === 0) {
      this.versionListTarget.innerHTML = this._emptyForContext()
      return
    }
    const html = this._versions.map(v =>
      `<label class="flex items-center gap-2 text-xs cursor-pointer">
         <input type="checkbox" class="checkbox checkbox-xs" name="version_ids[]" value="${v.id}" checked
                data-action="change->vehicle-localization#versionChanged">
         <span>${this._escape(v.name)}</span>
       </label>`
    ).join("")
    this.versionListTarget.innerHTML = html
  }

  renderModels() {
    if (!this.hasModelListTarget) return
    if (!this._versions || this._versions.length === 0) {
      this.modelListTarget.innerHTML = this._emptyForContext()
      return
    }
    const checkedVersionIds = this._checkedVersionIds()
    const versions = this._versions.filter(v => checkedVersionIds.includes(v.id))

    if (versions.length === 0) {
      this.modelListTarget.innerHTML = this._empty(this.pickVersionFirstValue)
      return
    }

    const html = versions.map(v => {
      const rows = v.models.map(m => {
        const label = `${this._escape(m.name)} ` +
          `<span class="text-xs text-base-content/40">${m.year || "?"}` +
          `${m.region_code ? ` · ${this._escape(m.region_code)}` : ""}</span>`
        return `<label class="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" class="checkbox checkbox-xs" name="model_ids[]" value="${m.id}" checked>
                  <span>${label}</span>
                </label>`
      }).join("")
      return `<div class="mb-2">
                <div class="text-xs font-semibold text-base-content/60 mb-1">${this._escape(v.name)}</div>
                ${rows}
              </div>`
    }).join("")

    this.modelListTarget.innerHTML = html || this._emptyForContext()
  }

  // Picks the right empty-state copy based on whether a target region is
  // chosen. With a target, empty means "nothing left to localize here";
  // without one, it's just "no versions found".
  _emptyForContext() {
    const targetId = this._currentTargetRegionId()
    if (targetId && this._targetRegionName) {
      const msg = this.fullyLocalizedValue.replace("%{region}", this._targetRegionName)
      return `<p class="text-xs text-success">${this._escape(msg)}</p>`
    }
    return this._empty(this.noVersionsValue)
  }

  _empty(text) {
    return `<p class="text-xs text-base-content/40">${this._escape(text)}</p>`
  }

  _checkedVersionIds() {
    if (!this.hasVersionListTarget) return []
    return Array.from(
      this.versionListTarget.querySelectorAll('input[type="checkbox"]:checked')
    ).map(el => el.value)
  }

  // Step 3 — bulk apply. Flips every row's radio to `decision`. The
  // inline editor (name/description/media) lives on Step 3b, not here.
  applyAll(event) {
    event.preventDefault()
    const decision = event.currentTarget.dataset.decision
    if (!decision) return
    if (!this.hasFeatureRowTarget) return
    this.featureRowTargets.forEach(row => {
      const radio = row.querySelector(`input[type="radio"][data-decision="${decision}"]`)
      if (radio) radio.checked = true
    })
  }

  // Playlist replicate checkbox: expose the name override input only when
  // the admin is actually replicating.
  playlistDecisionChanged(event) {
    const box = event.currentTarget
    const row = box.closest('[data-vehicle-localization-target="playlistRow"]')
    if (!row) return
    const overrides = row.querySelector('[data-vehicle-localization-target="playlistOverrides"]')
    if (overrides) overrides.classList.toggle("hidden", !box.checked)
  }

  _escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
